import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useClients } from '../hooks/useClients'
import { useAllowedDomains } from '../hooks/useAllowedDomains'
import { useShares } from '../hooks/useShares'
import { ClientForm } from '../components/ClientForm'
import { ClientCard } from '../components/ClientCard'
import { Plus, Trash2, Globe, Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr)
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  return `${String(d.getDate()).padStart(2, '0')}-${months[d.getMonth()]}-${d.getFullYear()}`
}

export function ClientsPage() {
  const { clients, loading, error, createClient, updateClient, deleteClient } = useClients()
  const { domains: allowedDomains, loading: domainsLoading, error: domainsError, addDomain, removeDomain } = useAllowedDomains()
  const { shares } = useShares()
  const [showForm, setShowForm] = useState(false)
  const [formError, setFormError] = useState('')
  const [newDomain, setNewDomain] = useState('')
  const [newDomainNote, setNewDomainNote] = useState('')
  const [domainFormError, setDomainFormError] = useState('')
  const [adminEmail, setAdminEmail] = useState('')
  const [adminName, setAdminName] = useState('')
  const [adminFormError, setAdminFormError] = useState('')

  const admins = clients.filter(c => c.role === 'admin')
  const nonAdminClients = clients.filter(c => c.role !== 'admin')

  // Count non-expired shares per client email (any workspace member match).
  const shareCountByEmail = shares
    .filter(s => !s.expired)
    .reduce<Record<string, number>>((acc, s) => {
      const emails = new Set<string>()
      for (const w of s.workspaces) {
        for (const m of w.members) {
          if (m.kind === 'email') emails.add(m.recipient)
        }
      }
      for (const email of emails) acc[email] = (acc[email] || 0) + 1
      return acc
    }, {})

  async function handleSave(data: { email: string; name: string; role: string; pin?: string }) {
    setFormError('')
    try {
      await createClient({ email: data.email, name: data.name, role: data.role })
      setShowForm(false)
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save')
    }
  }

  function handleCloseDialog() {
    setShowForm(false)
    setFormError('')
  }

  async function handleAddDomain(e: React.FormEvent) {
    e.preventDefault()
    setDomainFormError('')
    const domain = newDomain.trim().toLowerCase()
    if (!domain) return
    try {
      await addDomain(domain, newDomainNote.trim() || undefined)
      setNewDomain('')
      setNewDomainNote('')
    } catch (err) {
      setDomainFormError(err instanceof Error ? err.message : 'Failed to add domain')
    }
  }

  async function handleRemoveDomain(domain: string) {
    if (!confirm(`Remove allowed domain ${domain}?`)) return
    try {
      await removeDomain(domain)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to remove domain')
    }
  }

  async function handleAddAdmin(e: React.FormEvent) {
    e.preventDefault()
    setAdminFormError('')
    const trimEmail = adminEmail.trim().toLowerCase()
    if (!trimEmail) return

    try {
      const existing = clients.find(c => c.email === trimEmail)
      if (existing) {
        if (existing.role === 'admin') {
          setAdminFormError('Already an admin')
          return
        }
        await updateClient(trimEmail, { role: 'admin' } as any)
      } else {
        await createClient({ email: trimEmail, name: adminName.trim() || trimEmail.split('@')[0], role: 'admin' })
      }
      setAdminEmail('')
      setAdminName('')
    } catch (err) {
      setAdminFormError(err instanceof Error ? err.message : 'Failed to add admin')
    }
  }

  async function handleRemoveAdmin(email: string) {
    if (!confirm(`Remove admin privileges for ${email}? They will become a regular client.`)) return
    try {
      await updateClient(email, { role: 'client' } as any)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to remove admin')
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Users</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Manage registered users, admin privileges, and allowed domains.
        </p>
      </div>

      <Dialog open={showForm} onOpenChange={(open) => { if (!open) handleCloseDialog() }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Client</DialogTitle>
            <DialogDescription>
              Add a new client and assign seeds.
            </DialogDescription>
          </DialogHeader>
          <ClientForm
            client={null}
            error={formError}
            onSave={handleSave}
            onCancel={handleCloseDialog}
          />
        </DialogContent>
      </Dialog>

      <Tabs defaultValue="clients">
        <TabsList>
          <TabsTrigger value="clients">Clients</TabsTrigger>
          <TabsTrigger value="admin">Admin</TabsTrigger>
        </TabsList>

        {/* Clients tab */}
        <TabsContent value="clients" className="mt-6 space-y-6">
          {/* Allowed Domains card */}
          <Card>
            <CardContent className="p-4">
              <h3 className="text-sm font-medium text-foreground">Allowed Domains</h3>
              <p className="mt-1 mb-3 text-xs text-muted-foreground">
                Users from these domains can request a PIN and log in to the Seeds landing portal.
              </p>
              <form onSubmit={handleAddDomain} className="flex gap-3 items-end mb-3">
                <div className="flex-1">
                  <Input
                    type="text"
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                    placeholder="example.com"
                  />
                </div>
                <Input
                  type="text"
                  value={newDomainNote}
                  onChange={(e) => setNewDomainNote(e.target.value)}
                  placeholder="Note (optional)"
                  className="w-48"
                />
                <Button type="submit">
                  <Plus className="h-4 w-4" />
                  Add
                </Button>
              </form>
              {domainFormError && (
                <p className="mb-2 text-sm text-destructive">{domainFormError}</p>
              )}
              {domainsLoading ? (
                <div className="space-y-2">
                  {[1, 2].map(i => <Skeleton key={i} className="h-8 w-full" />)}
                </div>
              ) : domainsError ? (
                <p className="text-sm text-destructive">{domainsError}</p>
              ) : allowedDomains.length === 0 ? (
                <p className="text-sm text-muted-foreground">No allowed domains yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Domain</TableHead>
                      <TableHead>Note</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allowedDomains.map(d => (
                      <TableRow key={d.domain}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Globe className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm font-medium">{d.domain}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {d.note || '\u2014'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="xs"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleRemoveDomain(d.domain)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Remove
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Users card */}
          <Card>
            <CardContent className="p-4">
              <h3 className="text-sm font-medium text-foreground">Users</h3>
              <p className="mt-1 mb-3 text-xs text-muted-foreground">
                These users can request a PIN and log in to the Seeds landing portal.
              </p>
              <form onSubmit={(e) => { e.preventDefault(); setShowForm(true) }} className="flex gap-3 items-end mb-3">
                <Button type="button" onClick={() => setShowForm(true)}>
                  <Plus className="h-4 w-4" />
                  Add
                </Button>
              </form>
              {loading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-8 w-full" />)}
                </div>
              ) : error ? (
                <p className="text-sm text-destructive">{error}</p>
              ) : nonAdminClients.length === 0 ? (
                <p className="text-sm text-muted-foreground">No users yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Shares</TableHead>
                      <TableHead>Last Active</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {nonAdminClients.map((client) => (
                      <ClientCard
                        key={client.email}
                        client={client}
                        shareCount={shareCountByEmail[client.email] || 0}
                      />
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Admin tab */}
        <TabsContent value="admin" className="mt-6">
          <Card className="mb-6">
            <form onSubmit={handleAddAdmin} className="flex gap-3 items-end p-4">
              <div className="flex-1">
                <Input
                  type="email"
                  value={adminEmail}
                  onChange={e => setAdminEmail(e.target.value)}
                  placeholder="admin@example.com"
                />
              </div>
              <div className="w-48">
                <Input
                  type="text"
                  value={adminName}
                  onChange={e => setAdminName(e.target.value)}
                  placeholder="Name (optional)"
                />
              </div>
              <Button type="submit">
                <Plus className="h-4 w-4" />
                Add Admin
              </Button>
            </form>
            {adminFormError && (
              <p className="px-4 pb-3 text-sm text-destructive">{adminFormError}</p>
            )}
          </Card>

          {loading ? (
            <Card>
              <div className="p-4 space-y-3">
                {[1, 2].map(i => <Skeleton key={i} className="h-10 w-full" />)}
              </div>
            </Card>
          ) : error ? (
            <Alert variant="destructive">
              <AlertDescription>Error: {error}</AlertDescription>
            </Alert>
          ) : admins.length === 0 ? (
            <Card>
              <div className="p-8 text-center text-sm text-muted-foreground">
                No admin users yet. Add one above.
              </div>
            </Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {admins.map(admin => (
                    <TableRow key={admin.email} className="cursor-pointer hover:bg-muted/50">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                          <Link to={`/users/${encodeURIComponent(admin.email)}`} className="text-foreground hover:text-seed-green-600">
                            {admin.email}
                          </Link>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{admin.name}</TableCell>
                      <TableCell className="text-muted-foreground whitespace-nowrap">
                        {formatDate(admin.createdAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="xs"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleRemoveAdmin(admin.email)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
